import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LogIn, ClipboardCheck, School, X, ShieldCheck, Users,
  GraduationCap, Briefcase, ArrowRight, Phone, Lock, Eye,
  EyeOff, ArrowLeft, CheckCircle, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

const LANDING_OPTIONS = [
  {
    id: 'login',
    icon: LogIn,
    title: 'Login',
    desc: 'Access your account',
    primary: true,
  },
  {
    id: 'check-result',
    icon: ClipboardCheck,
    title: 'Check Result',
    desc: "Check your child's result",
    primary: false,
  },
  {
    id: 'get-started',
    icon: School,
    title: 'Get Started',
    desc: 'Get your school started for free',
    primary: false,
  },
];

const ROLES = [
  {
    id: 'teacher',
    icon: ShieldCheck,
    label: 'Teacher / Admin',
    desc: 'Login as a school admin or teacher',
    iconColor: 'text-[#2563eb]',
    iconBg: 'bg-blue-50',
  },
  {
    id: 'parent',
    icon: Users,
    label: 'Parent',
    desc: 'Monitor your child\'s academic progress',
    iconColor: 'text-emerald-600',
    iconBg: 'bg-emerald-50',
  },
  {
    id: 'student',
    icon: GraduationCap,
    label: 'Student',
    desc: 'Access your student dashboard',
    iconColor: 'text-amber-600',
    iconBg: 'bg-amber-50',
  },
  {
    id: 'partner',
    icon: Briefcase,
    label: 'Partner / Reseller',
    desc: 'Login to your affiliate portal',
    iconColor: 'text-purple-600',
    iconBg: 'bg-purple-50',
  },
];

export default function LandingPage() {
  const navigate = useNavigate();
  const { login } = useAuth();

  const [modal, setModal] = useState(false); // 'login' | false
  const [step, setStep] = useState('role'); // 'role' | 'parent-phone' | 'parent-password' | 'parent-create'

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPass, setConfirmPass] = useState('');
  const [showPass, setShowPass] = useState(false);
  const [hasPassword, setHasPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const openLogin = () => {
    setModal(true);
    setStep('role');
    setPhone(''); setPassword(''); setConfirmPass('');
    setErr('');
  };
  const closeModal = () => { setModal(false); setStep('role'); };

  const handleLanding = (id) => {
    if (id === 'login') openLogin();
    else if (id === 'check-result') navigate('/check-result');
    else navigate('/register');
  };

  const handleRole = (roleId) => {
    setErr('');
    if (roleId === 'teacher' || roleId === 'student') navigate('/login');
    else if (roleId === 'partner') navigate('/reseller/login');
    else { setStep('parent-phone'); }
  };

  const handlePhoneLookup = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { setErr('Please enter your phone number'); return; }
    setLoading(true); setErr('');
    try {
      const r = await axios.post(`${API}/api/public/parent/lookup`, { phone });
      const d = r.data.data;
      setHasPassword(d.has_password);
      setStep(d.has_password ? 'parent-password' : 'parent-create');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Phone number not found. Check with your school.');
    } finally { setLoading(false); }
  };

  const handleParentLogin = async (e) => {
    e.preventDefault();
    if (!password) { setErr('Please enter your password'); return; }
    setLoading(true); setErr('');
    try {
      const r = await axios.post(`${API}/api/public/parent/login`, { phone, password });
      const { token, user, school } = r.data;
      login(token, user, school);
      closeModal();
      navigate('/parent/dashboard');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Login failed. Please try again.');
    } finally { setLoading(false); }
  };

  const handleCreatePassword = async (e) => {
    e.preventDefault();
    if (!password) { setErr('Please enter a password'); return; }
    if (password.length < 6) { setErr('Password must be at least 6 characters'); return; }
    if (password !== confirmPass) { setErr('Passwords do not match'); return; }
    setLoading(true); setErr('');
    try {
      const r = await axios.post(`${API}/api/public/parent/set-password`, { phone, password });
      const { token, user, school } = r.data;
      login(token, user, school);
      toast.success('Account created! Welcome to EDUVA.');
      closeModal();
      navigate('/parent/dashboard');
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to set password. Please try again.');
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{ background: 'linear-gradient(135deg, #EFF6FF 0%, #E0EEFF 50%, #F0F7FF 100%)' }}
    >
      {/* ── Landing Card ── */}
      <div className="w-full max-w-md bg-white rounded-3xl shadow-2xl shadow-blue-100/60 overflow-hidden">
        {/* Header */}
        <div className="px-8 pt-9 pb-5 text-center border-b border-slate-50">
          <div
            className="w-12 h-12 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-md"
            style={{ background: 'linear-gradient(135deg, #2563eb, #1e40af)' }}
          >
            <School className="w-6 h-6 text-white" />
          </div>
          <h1 className="text-2xl font-black text-slate-900 tracking-tight">Welcome to EDUVA</h1>
          <p className="text-slate-500 text-sm mt-1">Choose how you'd like to get started.</p>
        </div>

        {/* Options */}
        <div className="px-6 py-5 space-y-3">
          {LANDING_OPTIONS.map(({ id, icon: Icon, title, desc, primary }) => (
            <button
              key={id}
              onClick={() => handleLanding(id)}
              data-testid={`landing-${id}`}
              className={`w-full flex items-center gap-4 px-5 py-4 rounded-2xl text-left transition-all duration-200 group ${
                primary
                  ? 'text-white shadow-lg shadow-[#2563eb]/25 hover:shadow-xl hover:shadow-[#2563eb]/30 active:scale-[0.98]'
                  : 'bg-slate-50 hover:bg-slate-100 active:scale-[0.98] border border-slate-100 hover:border-slate-200'
              }`}
              style={primary ? { background: 'linear-gradient(135deg, #2563eb 0%, #1e40af 100%)' } : {}}
            >
              <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${
                primary ? 'bg-white/20' : 'bg-white border border-slate-200'
              }`}>
                <Icon className={`w-5 h-5 ${primary ? 'text-white' : 'text-[#2563eb]'}`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-bold text-sm ${primary ? 'text-white' : 'text-slate-800'}`}>{title}</p>
                <p className={`text-xs mt-0.5 ${primary ? 'text-white/75' : 'text-slate-500'}`}>{desc}</p>
              </div>
              <ArrowRight className={`w-4 h-4 flex-shrink-0 transition-transform group-hover:translate-x-0.5 ${
                primary ? 'text-white/80' : 'text-slate-400'
              }`} />
            </button>
          ))}
        </div>

        <div className="px-6 pb-6 text-center">
          <p className="text-xs text-slate-400">
            Trusted by Nigerian schools &mdash; EDUVA School Portal
          </p>
        </div>
      </div>

      {/* ── Login Modal ── */}
      {modal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={closeModal} />
          <div className="relative w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden" data-testid="login-modal">

            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                {step !== 'role' && (
                  <button
                    onClick={() => { setStep('role'); setErr(''); setPhone(''); setPassword(''); setConfirmPass(''); }}
                    className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-slate-100 text-slate-500 transition-colors mr-1"
                  >
                    <ArrowLeft className="w-4 h-4" />
                  </button>
                )}
                <div>
                  <h2 className="font-black text-slate-900 text-base">
                    {step === 'role' && 'Who Are You?'}
                    {step === 'parent-phone' && 'Parent Login'}
                    {step === 'parent-password' && 'Enter Your Password'}
                    {step === 'parent-create' && 'Create Your Password'}
                  </h2>
                  {step === 'parent-phone' && <p className="text-xs text-slate-500 mt-0.5">Enter the phone number used during your child's admission</p>}
                  {step === 'parent-create' && <p className="text-xs text-slate-500 mt-0.5">Set a password to access your portal</p>}
                </div>
              </div>
              <button onClick={closeModal} className="w-8 h-8 rounded-xl flex items-center justify-center hover:bg-slate-100 text-slate-500 transition-colors" data-testid="close-login-modal">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 pb-6 pt-4">

              {/* ── Role Selector ── */}
              {step === 'role' && (
                <div className="space-y-2.5">
                  {ROLES.map(({ id, icon: Icon, label, desc, iconColor, iconBg }) => (
                    <button
                      key={id}
                      onClick={() => handleRole(id)}
                      data-testid={`role-${id}`}
                      className="w-full flex items-center gap-4 p-4 rounded-2xl bg-slate-50 hover:bg-slate-100 border border-slate-100 hover:border-slate-200 transition-all group active:scale-[0.98]"
                    >
                      <div className={`w-11 h-11 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
                        <Icon className={`w-5 h-5 ${iconColor}`} />
                      </div>
                      <div className="flex-1 text-left">
                        <p className="font-bold text-slate-800 text-sm">{label}</p>
                        <p className="text-xs text-slate-500 mt-0.5">{desc}</p>
                      </div>
                      <ArrowRight className="w-4 h-4 text-slate-400 group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
                    </button>
                  ))}
                </div>
              )}

              {/* ── Parent Phone Step ── */}
              {step === 'parent-phone' && (
                <form onSubmit={handlePhoneLookup} className="space-y-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Phone Number</label>
                    <div className="relative">
                      <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type="tel"
                        value={phone}
                        onChange={e => { setPhone(e.target.value); setErr(''); }}
                        placeholder="e.g. 08074272239"
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb] text-sm transition-all"
                        autoFocus
                        data-testid="parent-phone-input"
                      />
                    </div>
                    {err && <p className="text-red-600 text-xs mt-1.5 flex items-center gap-1">{err}</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60 transition-all active:scale-[0.98]"
                    style={{ background: 'linear-gradient(135deg, #2563eb, #1e40af)' }}
                    data-testid="phone-lookup-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Continue <ArrowRight className="w-4 h-4" /></>}
                  </button>
                </form>
              )}

              {/* ── Parent Enter Password ── */}
              {step === 'parent-password' && (
                <form onSubmit={handleParentLogin} className="space-y-4">
                  <div className="bg-blue-50 border border-blue-100 rounded-2xl p-3 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-[#2563eb] flex-shrink-0" />
                    <p className="text-xs text-[#2563eb] font-semibold">Phone verified: {phone}</p>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type={showPass ? 'text' : 'password'}
                        value={password}
                        onChange={e => { setPassword(e.target.value); setErr(''); }}
                        placeholder="Enter your password"
                        className="w-full pl-10 pr-10 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb] text-sm"
                        autoFocus
                        data-testid="parent-password-input"
                      />
                      <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                        {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {err && <p className="text-red-600 text-xs mt-1.5">{err}</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: 'linear-gradient(135deg, #2563eb, #1e40af)' }}
                    data-testid="parent-login-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Login to Parent Portal'}
                  </button>
                </form>
              )}

              {/* ── Parent Create Password ── */}
              {step === 'parent-create' && (
                <form onSubmit={handleCreatePassword} className="space-y-4">
                  <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-3">
                    <p className="text-xs text-emerald-700 font-semibold flex items-center gap-1.5">
                      <CheckCircle className="w-4 h-4 flex-shrink-0" />
                      Phone verified. Create a password to access your parent portal.
                    </p>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Create Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type={showPass ? 'text' : 'password'}
                        value={password}
                        onChange={e => { setPassword(e.target.value); setErr(''); }}
                        placeholder="Min 6 characters"
                        className="w-full pl-10 pr-10 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb] text-sm"
                        autoFocus
                        data-testid="create-password-input"
                      />
                      <button type="button" onClick={() => setShowPass(s => !s)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400">
                        {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-xs font-bold text-slate-600 mb-1.5">Confirm Password</label>
                    <div className="relative">
                      <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <input
                        type={showPass ? 'text' : 'password'}
                        value={confirmPass}
                        onChange={e => { setConfirmPass(e.target.value); setErr(''); }}
                        placeholder="Re-enter password"
                        className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#2563eb]/20 focus:border-[#2563eb] text-sm"
                        data-testid="confirm-password-input"
                      />
                    </div>
                    {err && <p className="text-red-600 text-xs mt-1.5">{err}</p>}
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full py-3 rounded-xl text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                    style={{ background: 'linear-gradient(135deg, #2563eb, #1e40af)' }}
                    data-testid="set-password-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Account & Login'}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
